import {
  AfterViewInit,
  Component,
  ComponentRef, Injector, Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {CommonModule} from "@angular/common";
import {PortalDynamicModule} from "./components/portal-dynamic.module";
import {RequestService} from "../../../../core/services/request/request.service";
import {RouterModule} from "@angular/router";
import {DynamicComponentService} from "../../../../core/services/dynamic-component/dynamic-component.service";
import {CatalogPageModule} from "../../../catalog-page/catalog-page.module";
import {PortalService} from "../../../../core/services/portal/portal.service";
import {PortalComponentsModule} from "../../portal-components.module";
import {PortalPageModule} from "../../portal-page.module";
import {PortalSearchModule} from "../portal-search/portal-search.module";
import {FormsModule} from "@angular/forms";
import {CoreModule} from "../../../../core/core.module";
import {PortalComponentModel} from "../../../../core/services/portal-data/portal-component.model";
import {UtilService} from "../../../../core/services/util/util.service";
import {SharedModule} from "../../../../shared/shared.module";
import {HighchartsChartModule} from "../../../../highcharts/highcharts-chart.module";

@Component({
  selector: 'sb-portal-dynamic',
  templateUrl: './portal-dynamic.component.html',
  styleUrls: ['./portal-dynamic.component.scss']
})
export class PortalDynamicComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('content', {read: ViewContainerRef, static: false}) content: ViewContainerRef;

  // Id of portal component instance
  @Input()
  id: string = "";

  // Parameters to configure this portal component instance
  @Input()
  parametersJSON: string = "";

  // If true this component is rendering within the preview part of the edit component modal
  @Input()
  preview: boolean = false;

  // The formio component associated with this angular component
  @Input()
  formioComponent: any;

  private componentRef: ComponentRef<any>;

  constructor(private dynamicComponentService: DynamicComponentService,
              private injector: Injector,
              private requestService: RequestService,
              private portalService: PortalService,
              private utilService: UtilService) { }

  ngOnInit(): void {
  }

  async ngAfterViewInit() {
    this.utilService.tickThen(async () => {
      const defaultImplementation = "";
      const defaultTemplate = `<sb-portal-placeholder [id]="'${this.id}'"></sb-portal-placeholder>`;
      const defaultStyles = "";
      const defaultData = {};

      let implementation = defaultImplementation;
      let template = defaultTemplate;
      let styles = defaultStyles;
      let data = defaultData;
      let parameters: any = {};
      let component: PortalComponentModel = null;

      try {

        component = this.portalService.getComponent(this.id);

        if (component) {
          implementation = component.Component || "";
          template = component.Template || "";
          styles = component.Styles || "";

          parameters = (this.parametersJSON !== null && this.parametersJSON !== undefined) ? JSON.parse(this.parametersJSON) : {};

          // Get the initial data from the server, defaulting to an empty object if undefined/null
          data = (await this.getComponentData(component, parameters, "init")) || {};
        }
      } catch (e) {
        console.error("Exception retrieving portal component: ", e);
      }

      const modules = [
        CommonModule,
        RouterModule,
        FormsModule,
        CoreModule,
        PortalDynamicModule,
        PortalComponentsModule,
        PortalPageModule,
        PortalSearchModule,
        CatalogPageModule,
        SharedModule,
        HighchartsChartModule
      ];

      this.componentRef =
        await this.dynamicComponentService.makeDynamicComponent({
          type: 'portal',
          id: this.id,
          content: this.content,
          injector: this.injector,
          modules: modules,
          template: template,
          styles: styles,
          implementation: implementation,
          parameters: parameters,
          data: data,
          formioComponent: this.formioComponent,
          dataFunction: (parameters: any, action: string): Promise<any> => {
            return this.getComponentData(component, parameters, action);
          }
        });
    });
  }

  /**
   * Method to execute the Server Script related to the Portal Component.
   *
   * @param component The portal component
   * @param parameters The parameters to pass to the server script.
   * @param action The action associated with the request. This parameter can be used to run arbitrary server actions
   *              from within the same server script (i.e. a variable called 'action' will be set that the script can use
   *              to perform different actions).
   */
  async getComponentData(component: PortalComponentModel, parameters: any, action: string = "init"): Promise<any> {
    const scriptRequestData = {
      id: this.id,
      parameters: parameters,
      parametersJSON: this.parametersJSON,
      preview: this.preview,
      action: action
    };

    // Short circuit if there is no server script as there are many components that don't have one.
    if (!component.ServerScript) {
      return {};
    }

    try {
      const scriptResponse: any = await this.requestService.createRequest()
        .controller("servicely.core.Portal")
        .method("getPortalComponentData")
        .data(scriptRequestData)
        .execute();

      const scriptResponseData = scriptResponse.data;

      if (scriptResponseData) {
        if (typeof scriptResponseData === "object") {
          return scriptResponseData;
        } else if (typeof scriptResponseData === "string") {
          try {
            return JSON.parse(scriptResponseData);
          } catch (e) {
            return scriptResponseData;
          }
        } else {
          return scriptResponseData;
        }
      }
    } catch (e) {
      console.error("Exception executing portal component server script: ", e);
      throw e;
    }
  }

  ngOnDestroy(): void {
    this.componentRef && this.componentRef.destroy();
  }
}
